import * as Sentry from "@sentry/browser";
import { makeAutoObservable } from "mobx";

import type { ResolvedRequestOptions } from "./generated/client/types.gen";

import {
  apiGetAuthLogout,
  apiGetAuthRefresh,
  apiGetInfo,
  apiGetLimiters,
  apiGetUsersMe,
  APIInfoResponse,
  apiPostAuthLogin,
  apiPutPatientsPatientIdApproveConsentPaper,
  apiPutPatientsPatientIdApproveConsentTelemonitoring,
  apiPutPatientsPatientIdRevokeConsentPaper,
  apiPutPatientsPatientIdRevokeConsentTelemonitoring,
  client,
  DefaultMedicationSchemaResponse,
  DefaultPatientResponse,
  FeatureFlagState,
  Level3PatientResponse,
  PermissionBit,
  SeerlinqEnvironments,
  UserFeatureFlag,
} from ".";
import { apiRoute } from "../config/config";
import { SearchParam } from "../constants/constants";
import {
  hasMedicalAccess,
  hasMonitoringAccess,
  hasPermission,
} from "../constants/permissions";
import { updateServerTimeOffset } from "../utils/server-time";
import { assert, Deferral } from "../utils/utils";

const sessionLoginTimeKey = "sessionLoginTime";
const sessionLastRefreshTime = "sessionLastRefreshTime";

export class ApiConnector {
  apiRoute: string;
  apiVersion: string;
  loggedIn: boolean;
  username: string | null;
  userUUID: string | null = null;
  permissions: PermissionBit[];
  featureFlags: Partial<Record<UserFeatureFlag, FeatureFlagState>>;
  apiInfo: APIInfoResponse | null;
  canIAddPatients: boolean;
  isProdEnv: boolean;
  canViewWaitingOrAuditRoom: boolean;
  promptPassword = false;

  private deferralOn428?: Deferral;
  private loggingOut = false;
  private refreshPromise?: Promise<boolean>;
  private preClonedRequests = new Map<Request, Request>();
  private responseCache = new Map<string, Response>();
  private requestOptionsMap = new Map<Request, RequestOptionsWithMeta>();

  constructor(apiRoute: string, apiVersion: string = "v1") {
    this.apiRoute = apiRoute;
    this.apiVersion = apiVersion;
    this.loggedIn = false;
    this.username = null;
    this.permissions = [];
    this.featureFlags = {};
    this.apiInfo = null;
    this.canIAddPatients = false;
    this.isProdEnv = false;
    this.canViewWaitingOrAuditRoom = false;
    makeAutoObservable(this);
    console.log("Initialized with route", apiRoute);
  }

  init() {
    client.setConfig({
      baseUrl: apiRoute,
      credentials: "include",
      // Wrapped `fetch` impl for cache
      fetch: async (input) => {
        if (typeof input !== "object" || "href" in input) {
          return fetch(input);
        }

        const options = this.requestOptionsMap.get(input);

        if (options?.meta?.useSessionCache) {
          const cachedResponse = this.responseCache.get(input.url);
          if (cachedResponse) {
            return cachedResponse.clone();
          }
        }

        const response = await fetch(input);

        if (options?.meta?.useSessionCache !== undefined) {
          this.responseCache.set(input.url, response.clone());
        }

        return response;
      },
    });

    client.interceptors.request.use(this.interceptRequest);
    client.interceptors.response.use(this.interceptResponse);
    client.interceptors.error.use(this.interceptError);
  }

  destroy() {
    client.interceptors.request.eject(this.interceptRequest);
    client.interceptors.response.eject(this.interceptResponse);
    client.interceptors.error.eject(this.interceptError);
  }

  versionedRoute() {
    return `${this.apiRoute}/api/${this.apiVersion}`;
  }

  requireUserUUID(): string {
    if (this.userUUID == null) {
      throw new Error("userUUID is not set — user is not logged in");
    }
    return this.userUUID;
  }

  /**
   * Whether a feature flag is on for the logged-in user. `GET /users/me` serves
   * the whole registry already merged with the API's code-side defaults, so an
   * absent flag means an API that does not know that flag yet - off, rather
   * than a default this side would have to keep in sync.
   */
  hasFeatureFlag(flag: UserFeatureFlag): boolean {
    return this.featureFlags[flag]?.value === true;
  }

  // headers

  private refreshHeaders() {
    const csrfToken = this.getCSRFToken("csrf_refresh_token");
    return { "X-CSRF-TOKEN": csrfToken };
  }

  private getCSRFToken(token: string): string | null {
    let csrfToken = null;
    const cookies = document.cookie.split(";");
    cookies.forEach((cookie) => {
      const [name, value] = cookie.trim().split("=");
      if (name === token) {
        csrfToken = decodeURIComponent(value);
      }
    });
    return csrfToken !== null ? csrfToken : null;
  }

  // auth flow

  async login(username: string, password: string) {
    await apiPostAuthLogin({
      body: {
        username: username,
        password: password,
      },
      meta: { ignore401: true } satisfies RequestMeta,
    });

    // eslint-disable-next-line no-restricted-syntax
    localStorage.setItem(sessionLoginTimeKey, new Date().toISOString());

    let redirect = "/";
    const redirectParam =
      new URL(location.href).searchParams.get(SearchParam.redirect) || "/";

    if (redirectParam) {
      // Simple validation to disallow redirect to other origins
      if (/^\/[^\s]*$/.test(redirect)) {
        redirect = redirectParam;
      } else {
        console.error("Invalid redirect param");
      }
    }

    console.log("Logged in");
    window.location.href = redirect;
  }

  async relogin(password: string) {
    assert(this.username, "Username is missing");

    await apiPostAuthLogin({
      body: {
        username: this.username,
        password: password,
      },
      meta: { ignore401: true, ignore428: true } satisfies RequestMeta,
    });

    this.deferralOn428?.resolve();
    this.deferralOn428 = undefined;
    this.promptPassword = false;
  }

  cancelPasswordPrompt() {
    this.deferralOn428?.reject();
    this.deferralOn428 = undefined;
    this.promptPassword = false;
  }

  private checkWaitingOrAuditRoomAccess() {
    return hasPermission(this.permissions, PermissionBit.VIEW_WAITING_ROOM);
  }

  private checkCanAddPatients(monitoringActive: boolean) {
    // Monitoring-level users can always add patients
    if (hasMonitoringAccess(this.permissions)) return true;
    // Physicians with active monitoring can add patients
    return hasMedicalAccess(this.permissions) && monitoringActive;
  }

  /** Check whether there is a valid session (without triggering logout on failure). */
  async hasSession(): Promise<boolean> {
    const { response } = await apiGetUsersMe({
      throwOnError: false,
      meta: { ignore401: true, ignore428: true } satisfies RequestMeta,
    });
    return response.ok;
  }

  async assumeLoggedIn() {
    await Promise.all([this.fetchMyInfo(), this.fetchApiInfo()]);
  }

  private async fetchMyInfo() {
    const { data } = await apiGetUsersMe();
    this.username = data.username;
    this.userUUID = data.uuid;
    this.permissions = data.access_policy?.permissions ?? [];
    this.featureFlags = data.feature_flags ?? {};
    this.canViewWaitingOrAuditRoom = this.checkWaitingOrAuditRoomAccess();
    this.canIAddPatients = this.checkCanAddPatients(
      data.access_policy?.monitoring_active ?? false,
    );

    this.loggedIn = true;
    Sentry.setUser({ id: data.uuid });
  }

  private async fetchApiInfo() {
    // eslint-disable-next-line no-restricted-syntax -- measuring RTT, not creating a timestamp
    const requestStart = Date.now();
    const info = await apiGetInfo();
    // eslint-disable-next-line no-restricted-syntax -- measuring RTT, not creating a timestamp
    const requestEnd = Date.now();
    this.apiInfo = info.data;
    // The /info spec types `environment` as a plain string, so compare against
    // the enum's string value rather than the enum member.
    this.isProdEnv =
      this.apiInfo.environment === (SeerlinqEnvironments.prod as string);

    // Calculate server time offset for timestamp synchronization
    updateServerTimeOffset(
      this.apiInfo.server_time_utc,
      requestStart,
      requestEnd,
    );
  }

  async logout(returnAfterLogin = false) {
    if (this.loggingOut) {
      return; // Prevent re-entry
    }

    this.loggingOut = true;
    await apiGetAuthLogout({
      throwOnError: false,
      meta: { ignore401: true, ignore428: true } satisfies RequestMeta,
    });
    localStorage.removeItem(sessionLoginTimeKey);
    localStorage.removeItem(sessionLastRefreshTime);
    console.log("Logged out");

    window.location.href =
      "/login" +
      (returnAfterLogin
        ? `?${SearchParam.redirect}=${encodeURIComponent(location.pathname)}`
        : "");
  }

  private async tryRefresh() {
    // This trick with storing the Promise prevents multiple simultaneous calls
    if (!this.refreshPromise) {
      const headers = this.refreshHeaders();
      this.refreshPromise = apiGetAuthRefresh({
        headers,
        meta: { ignore401: true, ignore428: true } satisfies RequestMeta,
      })
        .then(() => {
          localStorage.setItem(
            sessionLastRefreshTime,
            new Date().toISOString(), // eslint-disable-line no-restricted-syntax
          );
          return true;
        })
        .catch((error) => {
          if (error instanceof RequestError && error.response?.status === 401) {
            // 401 means the refresh token is probably expired
            this.addRefreshBreadcrumb(error);
            return false;
          } else {
            // Rethrow any other error. This shouldn't log out
            throw error;
          }
        })
        .finally(() => {
          this.refreshPromise = undefined;
        });
    }
    return this.refreshPromise;
  }

  private addRefreshBreadcrumb(error: RequestError) {
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Token refresh failed",
      level: "warning",
      data: {
        status_code: error.response?.status,
        responseBody: error.data,
        url: error.response?.url,
      },
    });
  }

  // API operations

  async getLimiters() {
    const limiters = await apiGetLimiters();
    return limiters.data;
  }

  async revokePaperConsent(patId: number) {
    await apiPutPatientsPatientIdRevokeConsentPaper({
      path: { patient_id: patId },
    });
    window.location.reload();
  }

  async revokeTeleConsent(patId: number) {
    await apiPutPatientsPatientIdRevokeConsentTelemonitoring({
      path: { patient_id: patId },
    });
    window.location.reload();
  }

  async approvePaperConsent(patId: number) {
    await apiPutPatientsPatientIdApproveConsentPaper({
      path: { patient_id: patId },
    });
    window.location.reload();
  }

  async approveTeleConsent(patId: number) {
    await apiPutPatientsPatientIdApproveConsentTelemonitoring({
      path: { patient_id: patId },
    });
    window.location.reload();
  }

  async streamingFetch(
    path: string,
    body: unknown,
    signal?: AbortSignal,
  ): Promise<Response> {
    const doFetch = () => {
      const csrfToken = this.getCSRFToken("csrf_access_token");
      return fetch(`${this.versionedRoute()}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken && { "X-CSRF-TOKEN": csrfToken }),
        },
        credentials: "include",
        body: JSON.stringify(body),
        signal,
      });
    };

    let response = await doFetch();
    if (response.status === 401) {
      const ok = await this.tryRefresh();
      if (!ok) {
        void this.logout(true);
        throw new Error("Authentication failed");
      }
      response = await doFetch();
    }
    return response;
  }

  clearResponseCache() {
    this.responseCache.clear();
  }

  private attachCSRFToken(request: Request) {
    // Add CSRF token for POST/PUT
    if (
      request.method === "POST" ||
      request.method === "PUT" ||
      request.method === "DELETE"
    ) {
      const token = this.getCSRFToken("csrf_access_token");
      if (token) {
        request.headers.set("X-CSRF-TOKEN", token);
      }
    }
  }

  private interceptRequest = (
    request: Request,
    options: RequestOptionsWithMeta,
  ) => {
    // Prepare a clone of the request in case we need to retry it (we cannot clone it after it has been used anymore)
    this.preClonedRequests.set(request, request.clone());
    this.requestOptionsMap.set(request, options);
    this.attachCSRFToken(request);
    return request;
  };

  private interceptResponse = async (
    response: Response,
    request: Request,
    options: RequestOptionsWithMeta,
  ) => {
    const clonedRequest = this.preClonedRequests.get(request);
    assert(clonedRequest, "Request clone not found");

    this.preClonedRequests.delete(request);
    this.requestOptionsMap.delete(request);

    if (response.status === 401 && !options.meta?.ignore401) {
      return this.handle401(clonedRequest, response);
    } else if (response.status === 428 && !options.meta?.ignore428) {
      return this.handle428(clonedRequest, response);
    } else {
      return response;
    }
  };

  private async handle401(request: Request, response: Response) {
    // Try refresh once
    const refreshSuccess = await this.tryRefresh();

    if (!refreshSuccess) {
      await this.captureLogoutEvent(response, "Refresh failed");
      void this.logout(true);
      return response;
    }

    // Retry fetch if refresh was successful
    response = await this.refetch(request);
    if (response.status === 401) {
      await this.captureLogoutEvent(response, "Retry after refresh failed");
      void this.logout(true);
    }

    if (response.status === 428) {
      return this.handle428(request, response);
    }

    return response;
  }

  private async captureLogoutEvent(response: Response, reason: string) {
    let responseBody: string | null = null;
    try {
      responseBody = await response.clone().text();
    } catch {
      // ignore
    }

    // eslint-disable-next-line no-restricted-syntax
    const now = Date.now();
    const loginTime = localStorage.getItem(sessionLoginTimeKey);
    const lastRefreshTime = localStorage.getItem(sessionLastRefreshTime);

    Sentry.captureMessage("User logged out due to 401", {
      level: "info",
      extra: {
        reason,
        loginTime,
        lastRefreshTime,
        sessionDurationMs: loginTime ? now - Date.parse(loginTime) : null,
        timeSinceLastRefreshMs: lastRefreshTime
          ? now - Date.parse(lastRefreshTime)
          : null,
        responseStatus: response.status,
        responseBody,
        requestUrl: response.url,
      },
    });

    await Sentry.flush(2000);
  }

  private async handle428(request: Request, response: Response) {
    this.promptPassword = true;

    if (!this.deferralOn428) {
      this.deferralOn428 = new Deferral();
    }

    try {
      await this.deferralOn428.promise;
    } catch {
      // Prompt cancelled => return the original response
      return response;
    }

    // Retry fetch
    return this.refetch(request);
  }

  private refetch(request: Request) {
    request = request.clone();
    this.attachCSRFToken(request);
    return fetch(request);
  }

  private interceptError = (
    error: unknown,
    response: Response,
    request: Request,
  ) => {
    this.requestOptionsMap.delete(request);
    return new RequestError(error, response, request);
  };
}

export class RequestError extends Error {
  name = "RequestError";

  constructor(
    readonly data: unknown,
    readonly response: Response,
    readonly request: Request,
  ) {
    super(`Status ${response.status} from ${request.method} ${request.url}`);
  }
}

/** This is the type we get from the `/patients` endpoint when we use `load_type: basic`. */
export type PatientDto = Level3PatientResponse | DefaultPatientResponse;

export type MedicationDto = DefaultMedicationSchemaResponse;

interface RequestOptionsWithMeta extends ResolvedRequestOptions {
  meta?: RequestMeta;
}

interface RequestMeta {
  ignore401?: boolean;
  ignore428?: boolean;
  useSessionCache?: boolean;
}
